import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { Component, OnInit, ChangeDetectionStrategy, Inject, NgZone, ViewChild, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { addHours, getHours, startOfDay } from 'date-fns';
import { defaultIfEmpty, first, map, shareReplay, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { SnackBarService } from '../../../../core-services/snack-bar/snack-bar.service';
import {CreateMerchantAdScheduleRequest, EditMerchantAdScheduleRequest, MerchantAd, MerchantAdSchedule} from '../../../../core/dtos/api';
import { AdManagementService } from '../../ad-management.service';
import { Validators } from '@angular/forms';
import { combineLatest, forkJoin, of, Subject } from 'rxjs';
import { MerchantApiService } from '../../../../core/services/api/merchant-api.service';
import { DashboardApiService } from '../../../../core/services/api/dashboard-api.service';
import { HttpErrorResponse } from '@angular/common/http';
import { newClientSideId, utcToMtTime } from '../../../../core/pure-utils/string-utils';
import { DomSanitizer } from '@angular/platform-browser';
import { merchantUrlValidator } from '../../../../core/pure-utils/validators';
import { isDate } from 'date-fns/isDate';
import { isNullOrEmpty } from '../../../../core/pure-utils/object-utils';
import {hourTimeOptions} from '../../../../core/core-variables';

interface RowValue {
  linkText: string;
  adTitle: string;
  destinationUrl: string;
  scheduleStartDateStartOfDay: Date;
  scheduleStartDateHour: number;
  promotionDetails: string;
  affiliateExclusive: string;
  couponCode: string;
  tags: string[];
  tagsInput: string;
  imageUrl: string;
  scheduleId: string;
  clientSideId: string;
}

@Component({
    templateUrl: './schedule-ads-dialog.component.html',
    styleUrls: ['./schedule-ads-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ScheduleAdsDialogComponent implements OnInit, OnDestroy {

  constructor(public dialogRef: MatDialogRef<ScheduleAdsDialogComponent>, @Inject(MAT_DIALOG_DATA) public merchantAd: MerchantAd, private fb: UntypedFormBuilder,
              private adManagementService: AdManagementService, private ngZone: NgZone, private snackBarService: SnackBarService,
              private merchantApiService: MerchantApiService, private dashboardApiService: DashboardApiService,
              private changeDetectorRef: ChangeDetectorRef, private domSanitizer: DomSanitizer) {}

  @ViewChild('autosize') autosize: CdkTextareaAutosize;

  ngOnDestroy$ = new Subject();
  separatorKeysCodes: number[] = [ENTER, COMMA];
  readonly hourTimeOptions = hourTimeOptions;
  savingInProgress = false;
  merchantId = this.merchantAd.merchant.merchant_id;
  currentAccount$Sub = this.dashboardApiService.currentAccount$.pipe(first()).subscribe(x => this.merchantId = x.entity_id);
  dataSource: UntypedFormGroup[] = [];

  merchantAdSchedules: MerchantAdSchedule[] = [];
  merchantAdSchedules$ = this.dashboardApiService.currentAccount$.pipe(first(),
    switchMap(x => this.merchantApiService.getMerchantAdSchedules(x.entity_id, this.merchantAd.merchant_ad_id, 'pending')));

  merchantAdSchedules$Sub = this.merchantAdSchedules$.subscribe(xs => {
    this.merchantAdSchedules = xs;
    xs.forEach(x => this.topControls.rows.push(this.fb.group(this.createControlRow(x))));
    this.dataSource = <UntypedFormGroup[]> this.topControls.rows.controls;
    this.changeDetectorRef.detectChanges();
    this.autosize?.resizeToFitContent(true);
  });

  merchantUrls$ = this.adManagementService.merchantWebsites$.pipe(takeUntil(this.ngOnDestroy$), shareReplay(1));

  createControlRow(adSchedule: Partial<MerchantAdSchedule>) {
    let controlRow = {
      linkText: this.fb.control(adSchedule?.link_text ?? '', [Validators.required, Validators.maxLength(255)]),
      adTitle: this.fb.control(adSchedule?.ad_title ?? '', [Validators.required, Validators.maxLength(255)]),
      destinationUrl: this.fb.control(adSchedule?.destination_url ?? '', [Validators.required, Validators.maxLength(255)],
        merchantUrlValidator(this.merchantUrls$)),
      scheduleStartDateStartOfDay: this.fb.control(adSchedule?.schedule_start_date == null ? null : startOfDay(adSchedule.schedule_start_date),
        Validators.required),
      scheduleStartDateHour: this.fb.control(adSchedule?.schedule_start_date == null ? 0 : getHours(adSchedule.schedule_start_date)),
      affiliateExclusive: this.fb.control(''),
      imageUrl: this.fb.control(adSchedule?.image_url),
      scheduleId: this.fb.control(adSchedule?.merchant_ad_schedule_id),
      clientSideId: this.fb.control(newClientSideId())
    };

    controlRow.destinationUrl.markAsTouched();
    controlRow.destinationUrl.updateValueAndValidity();

    if (this.merchantAd.ad_type === 'banner') {
      controlRow.linkText.removeValidators(Validators.required);
      controlRow.imageUrl.addValidators(Validators.required);

      controlRow.linkText.updateValueAndValidity();
      controlRow.imageUrl.updateValueAndValidity();
    }

    combineLatest([
      controlRow.scheduleStartDateStartOfDay.valueChanges.pipe(tap(() => controlRow.scheduleStartDateHour.markAsTouched()),
        startWith(controlRow.scheduleStartDateStartOfDay.value)),
      controlRow.scheduleStartDateHour.valueChanges.pipe(startWith(controlRow.scheduleStartDateHour.value)),
    ]).pipe(takeUntil(this.ngOnDestroy$)).subscribe(([startDateStartOfDay, startDateHour]: [Date, number]) => {
      if (!isDate(startDateStartOfDay)) {
        controlRow.scheduleStartDateHour.setErrors(null);
        return;
      }

      let mtNow = utcToMtTime(new Date());
      let startDate = addHours(startDateStartOfDay, startDateHour);

      if (startDate <= mtNow) {
        controlRow.scheduleStartDateHour.setErrors({futureDateTimeRequired: true});
      } else if (this.merchantAd.end_date != null && startDate > this.merchantAd.end_date) {
        controlRow.scheduleStartDateHour.setErrors({changeDateBeforeEndDate: true});
      } else {
        let errors = {...controlRow.scheduleStartDateHour.errors};
        delete errors.futureDateTimeRequired;
        delete errors.changeDateBeforeEndDate;

        if (isNullOrEmpty(errors)) {
          controlRow.scheduleStartDateHour.setErrors(null);
        }
      }

      this.changeDetectorRef.detectChanges();
    });

    return controlRow;
  }

  topControls = {
    rows: this.fb.array([])
  };
  topFormGroup = this.fb.group(this.topControls);

  onImgClick(clientSideId: string) {
    let fileInputElement = document.getElementById(`fileInput[${clientSideId}]`);
    if(fileInputElement) {
      fileInputElement.click();
    }
  }

  onFileInputChange(event, scheduleId: string, imageUrlControl: UntypedFormControl) {
    let fileInputElement: HTMLInputElement = event.currentTarget;
    let file = fileInputElement?.files?.[0];

    let invalidFileReset = (msg: string) => {
      fileInputElement.value = null;
      imageUrlControl.setValue(this.merchantAdSchedules.find(x => x.merchant_ad_schedule_id === scheduleId)?.image_url ?? this.merchantAd.image_url);
      this.snackBarService.openErrorSnackBar(msg);
      this.changeDetectorRef.detectChanges();
    };

    if (file) {
      let maxFileSize = 300 * 1024; // 300KB
      let lowerFileName = file.name.toLowerCase();

      if (!(lowerFileName.endsWith('.jpg') || lowerFileName.endsWith('.jpeg') || lowerFileName.endsWith('.gif') || lowerFileName.endsWith('.png'))) {
        invalidFileReset('The image must be a .jpg, .jpeg, .gif or .png type.');
        return;
      } else if (file.size > maxFileSize) {
        invalidFileReset('Max size of ad image is 300KB.');
        return;
      }

      let fileImageObjectUrl = URL.createObjectURL(file);
      let tempImg = new Image();
      tempImg.src = fileImageObjectUrl;

      tempImg.onload = () => {
        let originalWidth = this.merchantAd.image_width;
        let originalHeight = this.merchantAd.image_height;

        if (tempImg.naturalWidth !== originalWidth || tempImg.naturalHeight !== originalHeight) {
          window.URL.revokeObjectURL(fileImageObjectUrl);
          invalidFileReset('New ad image dimensions must match previous ad image.');
        } else {
          imageUrlControl.setValue(this.domSanitizer.bypassSecurityTrustUrl(fileImageObjectUrl));
          this.changeDetectorRef.detectChanges();
        }
      };
    }
  }

  resizeToFitContentSub = this.ngZone.onStable.pipe(take(1)).subscribe(() => this.autosize?.resizeToFitContent(true));

  onRemoveScheduleEntryClick(index: number) {
    this.topControls.rows.removeAt(index);
    this.dataSource = <UntypedFormGroup[]> [...this.topControls.rows.controls];
  };

  onAddDynamicUpdateClick() {
    let prePopSchedule: Partial<MerchantAdSchedule> = {
      link_text: this.merchantAd.link_text,
      ad_title: this.merchantAd.ad_title,
      destination_url: this.merchantAd.destination_url,
      image_url: this.merchantAd.image_url,
    };

    let newFormGroup = this.fb.group(this.createControlRow(prePopSchedule));
    newFormGroup.markAllAsTouched();

    this.topControls.rows.push(newFormGroup);
    this.dataSource = <UntypedFormGroup[]> [...this.topControls.rows.controls];
    this.changeDetectorRef.detectChanges();
    this.autosize?.resizeToFitContent(true);
  }

  onSaveClick() {
    this.savingInProgress = true;
    let rowValues = (<RowValue[]> this.topControls.rows.value);

    let trashedMerchantAdSchedules =
      this.merchantAdSchedules.filter(merchantAdSchedule => rowValues.every(row => merchantAdSchedule.merchant_ad_schedule_id !== row.scheduleId));

    let deleteSchedules = trashedMerchantAdSchedules.map(x =>
      this.merchantApiService.deleteMerchantAdSchedule(this.merchantId, this.merchantAd.merchant_ad_id, x.merchant_ad_schedule_id));

    let deleteSchedules$ = forkJoin(deleteSchedules).pipe(defaultIfEmpty(<MerchantAdSchedule[]> []));

    let editSchedules$ = of(rowValues).pipe(
      switchMap(rowValuesEmission => {
        let editSchedules = rowValuesEmission.filter(row => row.scheduleId != null).map(row => {
          let editMerchantAdScheduleRequest: EditMerchantAdScheduleRequest = {
            adTitle: row.adTitle,
            destinationUrl: row.destinationUrl,
            scheduleStartDate: row.scheduleStartDateStartOfDay && addHours(row.scheduleStartDateStartOfDay, row.scheduleStartDateHour),
          };

          if (this.merchantAd.ad_type === 'banner') {
            let fileInputElement = <HTMLInputElement> document.getElementById(`fileInput[${row.clientSideId}]`);
            editMerchantAdScheduleRequest.imageFile = fileInputElement?.files?.[0];
          } else {
            editMerchantAdScheduleRequest.linkText = row.linkText;
          }

          return this.merchantApiService.editMerchantAdSchedule(this.merchantId, this.merchantAd.merchant_ad_id, row.scheduleId, editMerchantAdScheduleRequest);
        });

        return forkJoin(editSchedules).pipe(defaultIfEmpty(<MerchantAdSchedule[]> []));
      })
    );

    let createSchedules$ = (this.merchantAd.ad_type === 'banner' ? this.adManagementService.getAdImage(this.merchantAd.image_url) : of(new Blob())).pipe(
      switchMap((currentAdImageBlob) => {
        let createSchedules = rowValues.filter(row => row.scheduleId == null).map(row => {
          let createMerchantAdScheduleRequest: CreateMerchantAdScheduleRequest = {
            adTitle: row.adTitle,
            destinationUrl: row.destinationUrl,
            scheduleStartDate: row.scheduleStartDateStartOfDay && addHours(row.scheduleStartDateStartOfDay, row.scheduleStartDateHour),
          };

          if (this.merchantAd.ad_type === 'banner') {
            let fileInputElement = <HTMLInputElement> document.getElementById(`fileInput[${row.clientSideId}]`);
            createMerchantAdScheduleRequest.imageFile = fileInputElement?.files?.[0] ??
              new File([currentAdImageBlob], this.merchantAd.ad_original_image_name, {type: currentAdImageBlob.type});
          } else {
            createMerchantAdScheduleRequest.linkText = row.linkText;
          }

          return this.merchantApiService.createMerchantAdSchedule(this.merchantId, this.merchantAd.merchant_ad_id, createMerchantAdScheduleRequest)
            .pipe(map(returnSchedule => <const> [returnSchedule, row.clientSideId]));
        });

        return forkJoin(createSchedules).pipe(defaultIfEmpty(<[MerchantAdSchedule, string][]> []));
      })
    );

    forkJoin([deleteSchedules$, editSchedules$, createSchedules$])
      .subscribe({
        next: (result) => {
          this.snackBarService.openSnackBar('Saved');
          this.dialogRef.close(true);
        },
        error: (errorResponse: HttpErrorResponse) => {
          this.savingInProgress = false;
          this.snackBarService.openErrorSnackBar(errorResponse?.error?.error ?? 'An Error Occurred');
          this.dialogRef.close(true);

        }
      });
  }

  ngOnInit() { }

  ngOnDestroy() {
    this.ngOnDestroy$.next(undefined);
    this.ngOnDestroy$.complete();
  }
}
